import { fetchChartData, getDemands, demandTracking, queryMaxRealDemand } from "./api/index.js";
import { unitApi } from "@/api/modules/device/index.js";
import { download } from "@/api/modules/download/index.js";
import { getGroupSettingList } from "../../../tabs/StrategyController/api.js";
import dayjs from "dayjs";
import { getChartOption } from "./linesChartOptions.js"

const $str = window.$str;

export class VirtualLoadTracking {
  constructor(stationId, options) {
    this.stationId = stationId; // 当前电站id
    this.startTime = options.startTime ?? dayjs().format("YYYY-MM-DD ") + "00:00:00";
    this.endTime = options.endTime ?? dayjs().format("YYYY-MM-DD ") + "23:59:59";
    this.mmdMonth = options.mmdMonth; // 【月最大需量】对应的月份
    this.currentUnitId = options.currentUnitId ?? ' '; // 被选中的方阵id，' '表示全部。
    this.isZh = options.isZh; // 是否为中文
    this.unitList = []; // 方阵列表。
    this.showDemandProtection = false; // 是否展示【需量保护阈值】曲线，由 groupSettingList 决定

    this.chartData = {
      gccmChartData: [], // 并网点功率
      gwmChartData: [], // 关口表功率
      ploadChartData: [], // 负载功率
      demand: 0,
      showDemandProtection: false, // 是否展示【需量保护阈值】曲线
      // 最大需量曲线信息
      maxDemandInfo: {
        month: dayjs(this.mmdMonth).format('YYYY年MM月'), // 最大需量曲线对应的月份
        name: `${dayjs(this.mmdMonth).format('YYYY年MM月')}${$str.getKey('数据分析/最大需量')}`,
        value: 0, // 指定月份最大需量的值
      },
      demandTracksChartData: [], // 多条【eLink运行需量】的曲线
    }
  }

  /**
   * 初始化图表
   * @param {*} chartObj
   */
  async initCharts(chartObj) {
    /** 获取图表数据
     * 1、并网点功率
     * 2、关口表功率
     * 3、负载功率
     * 4、需量
     * 5、电站下多条eLink运行需量的图表数据 >>> 1-1#eLink运行需量曲线 | 2-1#eLink运行需量曲线
     */
    try {
      // 先取分组信息，用于判断【需量保护阈值】曲线是否展示
      await this.getElinkGroupData();
      const p1 = this.getBaseChartData(); // 获取图表的基本曲线数据
      const p2 = this.getDemandsChartData(); // 获取图表的需量数据
      const p3 = this.getDemandTracksChartData(); // 获取电站下多条eLink运行需量的图表数据
      const p4 = this.getMaxDemandsChartDataFromLastMonth(); // 获取图表中选中月的最大需量数据
      await Promise.all([p1, p2, p3, p4]);
      this.renderCharts(chartObj);
    } catch (err) {
      console.error('负荷跟踪-虚拟表请求图表数据的相关接口-请求异常', err);
      throw err;
    }
  }

  /**
   * 更新电站id >>> 直接触发所有数据更新&图表刷新
   * @param {*} id
   */
  async updateStationId(id) {
    this.stationId = id;
    // 获取下拉选项的数据
    await this.getUnitList(this.stationId);
  }

  /**
   * 更新当前选择的时间范围
   * @param {*} st
   * @param {*} et
   */
  updateTimeRange(st, et) {
    this.startTime = dayjs(st).format("YYYY-MM-DD ") + "00:00:00";
    this.endTime = dayjs(et).format("YYYY-MM-DD ") + "23:59:59";
  }

  /**
   * 更新当前选中的方阵的unitId
   * @param {*} unitId
   */
  updateCurrentUnitId(unitId) {
    this.currentUnitId = unitId;
  }

  /**
   * 更新月份：图表上【最大曲线】对应的月份
   * >>> 用户可以在界面上自主选择
   * @param {*} month
   */
  undateMonthForMaxDemandLine(month) {
    this.mmdMonth = month;
    this.chartData.maxDemandInfo.month = dayjs(month).format('YYYY年MM月');
    this.chartData.maxDemandInfo.name = `${dayjs(month).format('YYYY年MM月')}${$str.getKey('数据分析/最大需量')}`;
  }

  /**
   * 更新图表
   * @param {*} chartObj
   */
  async updateCharts(chartObj) {
    await this.initCharts(chartObj);
  }

  /**
   * 执行图表的渲染
   * @param {*} chartObj eChart的实例
   */
  renderCharts(chartObj) {
    // 组件已卸载（图表实例被销毁）时，异步返回的数据不再渲染
    if (!chartObj || chartObj.isDisposed?.()) return;

    // 刷新前，检查当前MonthlyMaxDemandLine处于显示还是隐藏状态
    const isShowMmdLine = this.checkMmdlineIsShow(chartObj);

    // 将【需量保护阈值】曲线的显隐状态同步到 chartData，供 getChartOption 消费
    this.chartData.showDemandProtection = this.showDemandProtection;

    // 创建用于图表渲染的配置对象
    const options = getChartOption(this.chartData, this.startTime, this.endTime, 0, isShowMmdLine);
    // 将配置对象设置给eChart进行渲染
    chartObj.clear();
    chartObj.setOption(options);
  }

  /** ======【获取数据的方法】================================================================================================================= */
  /**
   * 获取elink分组策略
   * 空数组 → 无分组策略 → 展示【需量保护阈值】曲线
   * 非空数组 → 存在分组策略 → 不展示【需量保护阈值】曲线
   */
  async getElinkGroupData() {
    try {
      const res = await getGroupSettingList(this.stationId);
      if (res?.data && Array.isArray(res.data)) {
        this.showDemandProtection = res.data.length === 0;
      } else {
        this.showDemandProtection = false;
      }
    } catch (err) {
      console.error('获取elink分组策略失败：', err);
      this.showDemandProtection = false;
    }
  }

  /**
   * 获取图表的基本曲线数据：
   * gccm: 并网点功率
   * gwm: 关口表功率
   * pload: 负载功率
   * record_time: 采样时间
   * @returns
   */
  getBaseChartData() {
    return fetchChartData(this.startTime, this.endTime, this.stationId, this.currentUnitId).then(res => {
      if (res?.data && Array.isArray(res.data)) {
        const gccmChartData = []; // 并网点功率
        const gwmChartData = []; // 关口表功率
        const ploadChartData = []; // 负载功率
        res.data.forEach(it => {
          gccmChartData.push([it.record_time, it.gccm]);
          gwmChartData.push([it.record_time, it.gwm]);
          ploadChartData.push([it.record_time, it.pload]);
        });
        this.chartData.gccmChartData = gccmChartData;
        this.chartData.gwmChartData = gwmChartData;
        this.chartData.ploadChartData = ploadChartData;
      }
    })
  }

  /**
   * 获取需量的数据
   * currentUnitId为''时，表示当前选择的全部，从接口中取data.demandCurrent
   * @returns
   */
  getDemandsChartData() {
    return getDemands(this.stationId).then(res => {
      if (this.currentUnitId == ' ') {
        this.chartData.demand = res?.data?.demandCurrent ?? '';
      } else {
        if (res?.data?.elinkDemands && Array.isArray(res.data.elinkDemands)) {
          let demand = 0;
          res.data.elinkDemands.forEach(it => {
            if (this.currentUnitId == it.unitId) {
              demand += parseFloat(it.demandCurrent);
            }
          });
          this.chartData.demand = demand;
        }
      }
      if (Number.isNaN(parseFloat(this.chartData.demand))) {
        console.error('需量接口返回的结果中存在异常，请检查！\r\n 应返回data.elinkDemands，且为数值！ \r\n 返回的data.elinkDemands数组中的每个元素需包含demandCurrent字段，且为数值！')
      }
    })
  }

  /**
   * 获取用户选中月份中，最大的需量值。
   * @returns
   */
  getMaxDemandsChartDataFromLastMonth() {
    const params = {
      startTime: dayjs(this.mmdMonth).startOf('month').format('YYYY-MM-DD HH:mm:ss'),
      endTime: dayjs(this.mmdMonth).endOf('month').format('YYYY-MM-DD HH:mm:ss'),
      stationId: this.stationId
    }
    return queryMaxRealDemand(params).then(res => {
      this.chartData.maxDemandInfo.value = res;
    })
  }


  /**
   * 获取电站下多条eLink运行需量的数据
   * @returns
   */
  getDemandTracksChartData() {
    return demandTracking(this.stationId, this.startTime, this.endTime).then(res => {
      if (res?.data && Array.isArray(res.data)) {
        const demandTracksChartData = [];
        res.data.forEach(demandTrack => {
          if (demandTrack?.demandTrackingList && Array.isArray(demandTrack.demandTrackingList)) {
            const chartItem = {
              name: demandTrack.name + $str.getKey('数据分析/运行需量曲线'),
              chartData: demandTrack.demandTrackingList.map(p => {
                return [p.record_time, Number(p.demand).toFixed(2)];
              }),
            }
            demandTracksChartData.push(chartItem);
          }
        });
        this.chartData.demandTracksChartData = demandTracksChartData;
      }
    })
  }

  /** ======【辅助性功能方法】================================================================================================================= */
  /**
   * 获取方阵列表
   * @param {*} stationId
   */
  async getUnitList(stationId = this.stationId) {
    await unitApi.getUnitList(1, 9999, stationId).then(res => {
      if (res?.rows && Array.isArray(res.rows)) {
        this.unitList = res.rows;
      } else {
        console.error('获取unit/list的接口返回异常，请检查接口返回的res.rows是否为数组！');
        this.unitList = [];
      }
    }).catch(err => {
      console.error('获取unit/list的接口返回异常: ', err);
      this.unitList = [];
    });
  }

  /**
   * 导出数据
   * @param {*} options
   */
  async exportLoadTrackingData(options) {
    const url = `/dashboard/dataAnalysis/queryMeterPower/export`;
    const params = {
      startTime: options?.startTime ?? this.startTime,
      endTime: options?.endTime ?? this.endTime,
      stationId: options?.stationId ?? this.stationId,
      currentUnitId: options?.currentUnitId ?? this.currentUnitId,
      stationName: options?.stationName ?? '', // 拼接在excl-sheet名上的字段
      unitName: options?.unitName ?? '', // 拼接在excl-sheet名上的字段
    }
    await download(url, params, `load_tracking_${new Date().getTime()}.xlsx`);
  }


  /**
   * 检查【月最大需量】曲线的显隐状态
   * >>> 查询【月最大需量】刷新echarts前，保持刷新前的显隐
   * @param {*} chartObj
   * @returns
   */
  checkMmdlineIsShow(chartObj) {
    let isShow = false;
    const currentOptions = chartObj.getOption();
    if (Array.isArray(currentOptions?.legend) && currentOptions.legend.length > 0) {
      const legendList = Object.entries(chartObj.getOption().legend[0].selected);
      isShow = legendList.some(kvArr => {
        if (kvArr[0].includes('最大需量') && kvArr[1]) {
          return true;
        } else {
          return false;
        }
      })
    }
    return isShow;
  }
}
